import React, { useState, useMemo, useRef, useEffect } from "react"
import { DragDropProvider } from "@dnd-kit/react"
import { move } from "@dnd-kit/helpers"
import { Edit } from "lucide-react"
import { Modal } from "react-bootstrap"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiWorkspaceDeals, apiWorkspaceDealStages, apiWorkspaceSalesReps, apiWorkspaceDealTypes, apiVenues } from "~/routes"
import type Deal from "~/types/Deal"
import type DealType from "~/types/DealType"
import type SalesRep from "~/types/SapesRep"
import { useToast } from "~/utils/Toast"
import DroppableStageSection from "./DroppableStageSection"

const formatMoney = (value?: number | string | null) => {
    if (value == null) return "$0.00"
    const numValue = typeof value === "string" ? parseFloat(value) : value
    return `$${numValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

const searchFilters = {
    dealTypeId: "",
    dateFrom: "",
    dateTo: "",
    salesRepresentativeId: "",
}

interface DealStage {
    id: string
    name: string
    position: number
    colorCode: string
    stageType?: 'regular' | 'close_won' | 'close_lost'
}

const DealsPipelineList: React.FC = () => {
    const { venueSlug } = getAppConfig()
    const toast = useToast()

    const [filters, setFilters] = useState(searchFilters)
    const [dealsByStage, setDealsByStage] = useState<{ [key: string]: Deal[] }>({})
    const previousDeals = useRef<{ [key: string]: Deal[] }>({})

    // Sales Goal modal state
    const [showGoalModal, setShowGoalModal] = useState(false)
    const [salesGoalInput, setSalesGoalInput] = useState("")
    const [isSavingGoal, setIsSavingGoal] = useState(false)

    const buildQueryParams = () => {
        const params: any = {}

        if (filters.dealTypeId) {
            params["q[deal_type_id_eq]"] = filters.dealTypeId
        }

        if (filters.dateFrom) {
            params["q[expected_close_date_gteq]"] = filters.dateFrom
        }

        if (filters.dateTo) {
            params["q[expected_close_date_lteq]"] = filters.dateTo
        }

        if (filters.salesRepresentativeId) {
            params["q[sales_rep_id_eq]"] = filters.salesRepresentativeId
        }

        return params
    }

    const { data: deals, isLoading: dealsLoading, refetch } = useFetchResource<
        { deals: Deal[] },
        "deals"
        >(
        apiWorkspaceDeals.index.path({ venue_slug: venueSlug }),
        "deals",
        buildQueryParams()
    )

    // Fetch ALL deals without filters for Closed Won Total calculation
    const { data: allDeals, refetch: refetchAllDeals } = useFetchResource<
        { deals: Deal[] },
        "deals"
        >(
        apiWorkspaceDeals.index.path({ venue_slug: venueSlug }),
        "deals",
        {} // No filters - get all deals
    )

    const { data: dealStages, isLoading: stagesLoading } = useFetchResource<
        DealStage[],
        "deal_stages"
        >(
        apiWorkspaceDealStages.index.path({ venue_slug: venueSlug }),
        "deal_stages",
        {}
    )

    const { data: dealTypes, isLoading: typesLoading } = useFetchResource<
        { deal_types: DealType[] },
        "deal_types"
        >(
        apiWorkspaceDealTypes.index.path({ venue_slug: venueSlug }),
        "deal_types",
        {}
    )

    const { data: salesReps } = useFetchResource<
        { sales_reps: SalesRep[] },
        "sales_reps"
        >(
        apiWorkspaceSalesReps.index.path({ venue_slug: venueSlug }),
        "sales_reps",
        {}
    )

    // Fetch venue data for sales goal
    const { data: venueData, refetch: refetchVenue } = useFetchResource<
        { venue: { salesYearGoal: number } },
        "venue"
        >(
        apiVenues.show.path({ venueSlug }),
        "venue",
        {}
    )

    const normalizeDeals = () => {
        if (!dealStages || !deals) return

        const normalized = dealStages.reduce((acc, stage) => {
            acc[stage.id] = deals.filter(deal => deal.dealStage?.id === stage.id)
            return acc
        }, {} as { [key: string]: Deal[] })

        setDealsByStage(normalized)
    }

    useEffect(() => {
        if (deals && dealStages && !dealsLoading && !stagesLoading) {
            normalizeDeals()
        }
    }, [deals, dealStages, dealsLoading, stagesLoading])

    const handleDragStart = () => {
        previousDeals.current = dealsByStage
    }

    const handleDragOver = (event: any) => {
        const { source } = event.operation

        if (source?.type !== "deal") return

        setDealsByStage((currentDeals) => move(currentDeals, event))
    }

    const handleDragEnd = (event: any) => {
        const { source, target } = event.operation

        if (!target) return

        if (event.canceled) {
            if (source.type === "deal") {
                setDealsByStage(previousDeals.current)
            }
            return
        }

        if (target.type !== "deal") return

        const { sortable } = target
        const newStageId = sortable.group

        if (!newStageId) return

        const dealToUpdate = deals?.find(d => d.id === target.id)
        if (!dealToUpdate) return

        const url = apiWorkspaceDeals.update.path({
            venue_slug: venueSlug,
            id: dealToUpdate.prefixId
        })

        fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                deal: {
                    deal_stage_id: newStageId
                }
            }),
        })
            .then(response => {
                if (!response.ok) {
                    toast("Error moving deal", "danger")
                    setDealsByStage(previousDeals.current)
                } else {
                    // Refetch all deals to update closed won total
                    refetchAllDeals()
                }
            })
            .catch((error) => {
                console.error("Error moving deal:", error)
                toast("Error moving deal", "danger")
                setDealsByStage(previousDeals.current)
            })
    }

    const handleFilterChange = (field: string, value: string) => {
        setFilters({ ...filters, [field]: value })
    }

    const applyFilters = () => {
        refetch(buildQueryParams())
    }

    const clearFilters = () => {
        setFilters(searchFilters)
        refetch({})
    }

    const handleOpenGoalModal = () => {
        setSalesGoalInput(venueData?.salesYearGoal?.toString() || "0")
        setShowGoalModal(true)
    }

    const handleSaveGoal = async () => {
        setIsSavingGoal(true)

        const data = {
            venueSlug: venueSlug,
            venue: {
                sales_year_goal: parseFloat(salesGoalInput) || 0
            }
        }

        const req = apiVenues.update({ data })

        req.then(() => {
            toast("Sales goal updated successfully", "success")
            setShowGoalModal(false)
            refetchVenue()
        })
            .catch((res) => {
                const errorMessage = res.body?.errors?.join(", ") || res.message || "Unknown error"
                toast(`Error updating sales goal. ${errorMessage}`, "danger")
            })
            .finally(() => {
                setIsSavingGoal(false)
            })
    }

    const stageTotals = useMemo(() => {
        const totals: Record<string, number> = {}

        Object.entries(dealsByStage).forEach(([stageId, stageDeals]) => {
            totals[stageId] = stageDeals.reduce((sum, deal) => {
                const value = typeof deal.value === "string" ? parseFloat(deal.value) : deal.value
                return sum + (value || 0)
            }, 0)
        })

        return totals
    }, [dealsByStage])

    const repsOptions = useMemo(() => {
        return (salesReps || [])
            .slice()
            .sort((a, b) => a.fullName.localeCompare(b.fullName))
    }, [salesReps])

    const totalPipelineValue = useMemo(() => {
        return Object.values(stageTotals).reduce((sum, value) => sum + value, 0)
    }, [stageTotals])

    const closedWonTotal = useMemo(() => {
        if (!dealStages || !allDeals) return 0

        const closedWonStageIds = dealStages
            .filter(s => s.stageType === 'close_won')
            .map(s => s.id)

        return allDeals.reduce((sum, deal) => {
            if (deal.dealStage && closedWonStageIds.includes(deal.dealStage.id)) {
                const value = typeof deal.value === "string" ? parseFloat(deal.value) : deal.value
                return sum + (value || 0)
            }
            return sum
        }, 0)
    }, [dealStages, allDeals])

    const salesGoal = venueData?.salesYearGoal || 0
    const pipelineVsGoal = totalPipelineValue - salesGoal
    const closedWonVsGoal = closedWonTotal - salesGoal

    const isLoading = dealsLoading || stagesLoading || typesLoading

    return (
        <LoadingWrapper isLoading={isLoading}>
            <PageTitle title="Deals Pipeline" newItemPath="/deals/new" newItemText="New Deal" />

            <div className="row mb-3">
                <div className="col-12">
                    <div className="card border-secondary" style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
                        <div className="card-body">
                            <div className="row g-3 align-items-end">
                                <div className="col-md-2">
                                    <label className="form-label">Deal Type</label>
                                    <select
                                        className="form-select"
                                        value={filters.dealTypeId}
                                        onChange={(e) => handleFilterChange("dealTypeId", e.target.value)}
                                    >
                                        <option value="">All Types</option>
                                        {dealTypes?.map(dealType => (
                                            <option key={dealType.id} value={dealType.id}>
                                                {dealType.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Close Date From</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.dateFrom}
                                        onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Close Date To</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.dateTo}
                                        onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Sales Representative</label>
                                    <select
                                        className="form-select"
                                        value={filters.salesRepresentativeId}
                                        onChange={(e) => handleFilterChange("salesRepresentativeId", e.target.value)}
                                    >
                                        <option value="">All Representatives</option>
                                        {repsOptions.map((rep) => (
                                            <option key={rep.id} value={rep.id}>
                                                {rep.fullName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <button
                                        className="btn btn-primary me-2"
                                        onClick={applyFilters}
                                    >
                                        Apply Filters
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={clearFilters}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pipeline Overview */}
            <div className="row mb-3">
                <div className="col-12">
                    <CatchCard
                        title={
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <span>Pipeline Overview</span>
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleOpenGoalModal}
                                >
                                    <Edit size={14} className="me-1" />
                                    Edit Goal
                                </button>
                            </div>
                        }
                    >
                        <div className="row text-center">
                            <div className="col-md-2">
                                <h5 className="text-muted mb-1 small">Total Pipeline Value</h5>
                                <h3 className="text-primary mb-0">{formatMoney(totalPipelineValue)}</h3>
                                <small className="text-muted">{deals?.length || 0} deals</small>
                            </div>
                            <div className="col-md-2">
                                <h5 className="text-muted mb-1 small">Yearly Sales Goal</h5>
                                <h3 className="text-warning mb-0">{formatMoney(salesGoal)}</h3>
                                <small className="text-muted">Target for year</small>
                            </div>
                            <div className="col-md-2">
                                <h5 className="text-muted mb-1 small">Pipeline vs Goal</h5>
                                <h3 className={pipelineVsGoal >= 0 ? "text-success mb-0" : "text-danger mb-0"}>
                                    {pipelineVsGoal >= 0 ? '+' : ''}{formatMoney(pipelineVsGoal)}
                                </h3>
                                <small className="text-muted">
                                    {pipelineVsGoal >= 0 ? 'Above' : 'Below'} goal
                                </small>
                            </div>
                            <div className="col-md-2">
                                <h5 className="text-muted mb-1 small">Closed Won Total</h5>
                                <h3 className="text-success mb-0">{formatMoney(closedWonTotal)}</h3>
                                <small className="text-muted">Won this year</small>
                            </div>
                            <div className="col-md-2">
                                <h5 className="text-muted mb-1 small">Closed Won vs Goal</h5>
                                <h3 className={closedWonVsGoal >= 0 ? "text-success mb-0" : "text-danger mb-0"}>
                                    {closedWonVsGoal >= 0 ? '+' : ''}{formatMoney(closedWonVsGoal)}
                                </h3>
                                <small className="text-muted">
                                    {closedWonVsGoal >= 0 ? 'Hit' : 'Need'} {formatMoney(Math.abs(closedWonVsGoal))}
                                </small>
                            </div>
                            <div className="col-md-2">
                                <h5 className="text-muted mb-1 small">Goal Progress</h5>
                                <h3 className="text-info mb-0">
                                    {salesGoal > 0 ? Math.round((closedWonTotal / salesGoal) * 100) : 0}%
                                </h3>
                                <small className="text-muted">Of yearly goal</small>
                            </div>
                        </div>
                    </CatchCard>
                </div>
            </div>

            {Object.keys(dealsByStage).length > 0 ? (
                <DragDropProvider
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                >
                    <div className="row flex-nowrap" style={{ overflowX: 'auto' }}>
                        {dealStages?.sort((a, b) => a.position - b.position).map((stage) => {
                            const stageDeals = dealsByStage[stage.id] || []

                            return (
                                <DroppableStageSection
                                    key={stage.id}
                                    stage={stage}
                                    deals={stageDeals}
                                    totalValue={stageTotals[stage.id] || 0}
                                />
                            )
                        })}
                    </div>
                </DragDropProvider>
            ) : (
                <div className="text-center py-4">
                    <p className="text-muted mb-0">No deals have been created yet</p>
                </div>
            )}

            {/* Edit Sales Goal Modal */}
            <Modal show={showGoalModal} onHide={() => setShowGoalModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Yearly Sales Goal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label">Yearly Sales Goal ($)</label>
                        <input
                            type="number"
                            className="form-control"
                            value={salesGoalInput}
                            onChange={(e) => setSalesGoalInput(e.target.value)}
                            placeholder="500000"
                            step="1000"
                            min="0"
                        />
                        <div className="form-text">
                            Set your team's revenue target for this year
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowGoalModal(false)}
                        disabled={isSavingGoal}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSaveGoal}
                        disabled={isSavingGoal}
                    >
                        {isSavingGoal ? 'Saving...' : 'Save Goal'}
                    </button>
                </Modal.Footer>
            </Modal>
        </LoadingWrapper>
    )
}

export default DealsPipelineList